import React, { useEffect, useState } from 'react';
import { Typography,
    Card,
    CardContent,
    Box,
    Button,
    CircularProgress,
    TextField,
    IconButton,
    Divider,
    Avatar,
    Chip
} from '@mui/material';
import { PieChart, pieArcLabelClasses } from '@mui/x-charts/PieChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { ShowChartOutlined,
    SentimentNeutral,
    SentimentSatisfiedAltOutlined,
    SentimentVeryDissatisfied,
    CommentOutlined,
    TrendingUp,
    Schedule,
    AutoAwesome,
    Send,
    SmartToy,
    Person
} from '@mui/icons-material';
import { useParams } from 'react-router-dom'
import PageWithTitleLayout from '../Layout/PageWithTitleLayout';
import { dummyFeedbackData } from './feedback';
import { dummySalesData } from './sales';
import { dummyHoursData } from './hours';
import ReportSummarizer from '../openai-stuff/reportSummarizer';
import { evaluationPieLabelProps } from '../../Utilities/evaluationArcLabel';
import MarkdownIt from 'markdown-it';

const md = new MarkdownIt();

const FeedbackManagementsDemo = () => {
    const { calendarYearMonth, employmentId } = useParams()
    const [ratingRatios, setRatingRatios] = useState()
    const [ratingCounts, setRatingCounts] = useState()
    const [feedbacks, setFeedbacks] = useState([])
    const [salesData, setSalesData] = useState([])
    const [hoursData, setHoursData] = useState([])
    const [monthlySummary, setMonthlySummary] = useState('')
    const [isGeneratingSummary, setIsGeneratingSummary] = useState(false)
    const [isAIGenerated, setIsAIGenerated] = useState(false)
    const [conversationHistory, setConversationHistory] = useState([])
    const [currentQuestion, setCurrentQuestion] = useState('')
    const [isGeneratingResponse, setIsGeneratingResponse] = useState(false)
    const [dynamicSuggestions, setDynamicSuggestions] = useState([])
    const [isGeneratingSuggestions, setIsGeneratingSuggestions] = useState(false)
    const year = calendarYearMonth.slice(0, 4)
    const month = calendarYearMonth.slice(5, 7)
    const formattedCalendarYearMonth = `${year}年${month}月`

    const iconMapping = {
        good: <SentimentSatisfiedAltOutlined />,
        neutral: <SentimentNeutral />,
        bad: <SentimentVeryDissatisfied />
    }

    const reportSummarizer = new ReportSummarizer();
    
    useEffect(() => {
        // Use dummy data instead of API call
        const res = { data: dummyFeedbackData }
        
        let good = 0
        let neutral = 0
        let bad = 0
        let comments = []
        res.data.forEach(feedback => {
            if (feedback.rating === 'good') {
                good++
            } else if (feedback.rating === 'neutral') {
                neutral++
            } else {
                bad++
            }
            comments.push(feedback.comment)
        })
        setRatingCounts({ good, neutral, bad })
        setRatingRatios({
            good: Math.round(good / res.data.length * 100),
            neutral: Math.round(neutral / res.data.length * 100),
            bad: Math.round(bad / res.data.length * 100)
        })
        setFeedbacks(res.data)
        
        // Set sales data
        setSalesData(dummySalesData)
        
        // Set hours data
        setHoursData(dummyHoursData)
    }, [calendarYearMonth, employmentId])

    // Generate AI summary
    const handleGenerateSummary = async () => {
        setIsGeneratingSummary(true);
        try {
            const summary = await reportSummarizer.generateSummary(feedbacks, salesData, hoursData);
            setMonthlySummary(summary);
            setIsAIGenerated(true);
            
            // Initialize dynamic suggestions for the first time
            setDynamicSuggestions([
                '売上の改善点は？',
                '労働時間の効率性は？',
                'チーム評価の傾向は？',
                '来月の改善提案は？'
            ]);
        } catch (error) {
            console.error('Failed to generate summary:', error);
            const fallbackSummary = generateFallbackSummary();
            setMonthlySummary(fallbackSummary);
            setIsAIGenerated(true);
            
            // Initialize dynamic suggestions even for fallback
            setDynamicSuggestions([
                '売上の改善点は？',
                '労働時間の効率性は？',
                'チーム評価の傾向は？',
                '来月の改善提案は？'
            ]);
        } finally {
            setIsGeneratingSummary(false);
        }
    };

    // Generate a basic fallback summary when AI fails
    const generateFallbackSummary = () => {
        let summary = `今月の総合評価（自動生成）\n\n`;
        
        if (feedbacks.length > 0) {
            const goodCount = feedbacks.filter(f => f.rating === 'good').length;
            const badCount = feedbacks.filter(f => f.rating === 'bad').length;
            const satisfactionRate = Math.round((goodCount / feedbacks.length) * 100);
            
            summary += `📊 チーム評価: ${satisfactionRate}%の満足度\n`;
            summary += `- 良い評価: ${goodCount}件\n`;
            summary += `- 改善が必要: ${badCount}件\n\n`;
        }
        
        if (salesData.length > 0) {
            const totalSales = salesData.reduce((sum, sale) => sum + sale.sales_amount, 0);
            const avgSales = Math.round(totalSales / salesData.length);
            summary += `💰 売上実績: 平均${avgSales.toLocaleString()}円/日\n`;
        }
        
        if (hoursData.length > 0) {
            const totalHours = hoursData.reduce((sum, hour) => sum + hour.actual_hours, 0);
            const avgHours = (totalHours / hoursData.length).toFixed(1);
            summary += `⏰ 労働時間: 平均${avgHours}時間/日\n\n`;
        }
        
        summary += `✅ ステータス: データが正常に収集されています\n`;
        summary += `🔧 エラー: AI要約機能の接続を確認してください\n`;
        
        return summary;
    };

    // Handle follow-up questions
    const handleFollowUpQuestion = async () => {
        if (!currentQuestion.trim()) return;
        
        const question = currentQuestion.trim();
        setCurrentQuestion('');
        
        // Add user question to conversation history
        const userMessage = {
            type: 'user',
            content: question,
            timestamp: new Date().toLocaleTimeString()
        };
        
        setConversationHistory(prev => [...prev, userMessage]);
        setIsGeneratingResponse(true);
        
        try {
            const response = await reportSummarizer.generateFollowUpResponse(
                question, 
                conversationHistory, 
                feedbacks, 
                salesData, 
                hoursData
            );
            
            // Add AI response to conversation history
            const aiMessage = {
                type: 'ai',
                content: response,
                timestamp: new Date().toLocaleTimeString()
            };
            
            setConversationHistory(prev => [...prev, aiMessage]);
        } catch (error) {
            console.error('Failed to generate follow-up response:', error);
            const fallbackResponse = reportSummarizer.generateFallbackResponse(question);
            
            const aiMessage = {
                type: 'ai',
                content: fallbackResponse,
                timestamp: new Date().toLocaleTimeString()
            };
            
            setConversationHistory(prev => [...prev, aiMessage]);
        } finally {
            setIsGeneratingResponse(false);
        }
    };

    // Generate dynamic suggestions when conversation history changes
    useEffect(() => {
        if (monthlySummary && conversationHistory.length > 0) {
            generateDynamicSuggestions();
        }
    }, [conversationHistory, monthlySummary]);

    // Handle Enter key press in question input
    const handleQuestionKeyPress = (event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            handleFollowUpQuestion();
        }
    };

    // Generate dynamic suggestions based on conversation history
    const generateDynamicSuggestions = async () => {
        if (conversationHistory.length === 0) {
            // Use static suggestions for initial state
            setDynamicSuggestions([
                '売上の改善点は？',
                '労働時間の効率性は？',
                'チーム評価の傾向は？',
                '来月の改善提案は？'
            ]);
            return;
        }

        setIsGeneratingSuggestions(true);
        try {
            const suggestions = await reportSummarizer.generateQuestionSuggestions(
                conversationHistory,
                feedbacks,
                salesData,
                hoursData
            );
            
            // Parse the suggestions from the AI response
            const parsedSuggestions = parseSuggestionsFromResponse(suggestions);
            setDynamicSuggestions(parsedSuggestions);
        } catch (error) {
            console.error('Failed to generate dynamic suggestions:', error);
            const fallbackSuggestions = reportSummarizer.generateFallbackSuggestions(conversationHistory);
            setDynamicSuggestions(fallbackSuggestions);
        } finally {
            setIsGeneratingSuggestions(false);
        }
    };

    // Parse suggestions from AI response
    const parseSuggestionsFromResponse = (response) => {
        try {
            // Try to extract numbered suggestions from the response
            const lines = response.split('\n');
            const suggestions = [];
            
            lines.forEach(line => {
                const match = line.match(/^\d+\.\s*(.+)$/);
                if (match && match[1].trim()) {
                    suggestions.push(match[1].trim());
                }
            });
            
            // If we found suggestions, return them
            if (suggestions.length > 0) {
                return suggestions.slice(0, 4); // Limit to 4 suggestions
            }
            
            // Fallback: try to extract questions ending with ?
            const questionMatches = response.match(/[^。！？]*[？?]/g);
            if (questionMatches && questionMatches.length > 0) {
                return questionMatches.slice(0, 4).map(q => q.trim());
            }
            
            // Final fallback: return default suggestions
            return [
                'より詳細な分析を教えてください',
                '具体的な改善策はありますか？',
                'データの傾向について詳しく説明してください',
                '他に気になる点はありますか？'
            ];
        } catch (error) {
            console.error('Error parsing suggestions:', error);
            return [
                'より詳細な分析を教えてください',
                '具体的な改善策はありますか？',
                'データの傾向について詳しく説明してください',
                '他に気になる点はありますか？'
            ];
        }
    };

    // Prepare sales data for bar chart
    const salesChartData = salesData.map(sale => ({
        date: sale.date.slice(-2), // Get just the day
        sales: sale.sales_amount / 1000, // Convert to thousands for better display
        prediction: sale.prediction ? sale.prediction / 1000 : null // Convert prediction to thousands
    }))

    // Prepare hours data for chart
    const hoursChartData = hoursData.map(hour => ({
        date: hour.date.slice(-2), // Get just the day
        actual: hour.actual_hours,
        planned: hour.planned_hours
    }))

    // Get weekly dates for x-ticks (every 7 days)
    const weeklyDates = salesChartData.filter((_, index) => index % 7 === 0).map(item => item.date)
    const weeklyHoursDates = hoursChartData.filter((_, index) => index % 7 === 0).map(item => item.date)

    // Calculate dynamic rows based on content and source
    const calculateDynamicRows = (text, isAI = false) => {
        if (!text) return 4;
        
        const lines = text.split('\n').length;
        
        // AI-generated content typically needs more space
        if (isAI) {
            const estimatedLines = Math.max(6, Math.min(lines + 3, 20));
            return estimatedLines;
        }
        
        // Manual content
        const estimatedLines = Math.max(4, Math.min(lines + 1, 12));
        return estimatedLines;
    };

    return (
        <PageWithTitleLayout title="総合評価グラフ - ChatGPTバージョン" disableStoreSelect>
            <Typography sx={{ ml: 5, mt: 5 }} variant="h5" fontWeight="bold">
                {formattedCalendarYearMonth}
            </Typography>

            <Card sx={{ m: 5 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" component="div" sx={{ display: 'flex', alignContent: 'center', fontWeight: 'bold' }}>
                            今月のまとめ
                        </Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleGenerateSummary}
                            disabled={isGeneratingSummary}
                            startIcon={isGeneratingSummary ? <CircularProgress size={20} /> : <AutoAwesome />}
                            sx={{ minWidth: '140px', height: '40px' }}
                        >
                            {isGeneratingSummary ? '生成中...' : 'AI要約生成'}
                        </Button>
                    </Box>
                    <Box sx={{ 
                        border: '1px solid #e0e0e0', 
                        borderRadius: 1, 
                        p: 2,
                        backgroundColor: isAIGenerated ? '#f8f9fa' : 'white',
                        minHeight: `${calculateDynamicRows(monthlySummary, isAIGenerated) * 24}px`
                    }}>
                        {monthlySummary ? (
                            <div 
                                dangerouslySetInnerHTML={{ __html: md.render(monthlySummary) }}
                                style={{
                                    lineHeight: '1.6',
                                    fontSize: '14px'
                                }}
                            />
                        ) : (
                            <Typography color="textSecondary">
                                今月の総合的な評価や改善点、来月への課題などを記入してください...
                            </Typography>
                        )}
                    </Box>
                    
                    {/* Q&A Section */}
                    {monthlySummary && (
                        <Box sx={{ mt: 3 }}>
                            <Divider sx={{ mb: 2 }} />
                            <Typography variant="h7" component="div" sx={{ display: 'flex', alignItems: 'center', fontWeight: 'bold', mb: 2 }}>
                                <SmartToy sx={{ mr: 1, color: 'primary.main' }} />
                                AIアシスタントに質問する
                            </Typography>
                            
                            {/* Conversation History */}
                            {conversationHistory.length > 0 && (
                                <Box sx={{ mb: 2, maxHeight: '500px', overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 2, backgroundColor: '#fafafa' }}>
                                    {conversationHistory.map((message, index) => (
                                        <Box key={index} sx={{ mb: 2, display: 'flex', gap: 1 }}>
                                            <Avatar sx={{ 
                                                width: 32, 
                                                height: 32, 
                                                bgcolor: message.type === 'user' ? 'primary.main' : 'secondary.main'
                                            }}>
                                                {message.type === 'user' ? <Person /> : <SmartToy />}
                                            </Avatar>
                                            <Box sx={{ flex: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                                        {message.type === 'user' ? 'あなた' : 'AIアシスタント'}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        {message.timestamp}
                                                    </Typography>
                                                </Box>
                                                <Typography variant="body2" sx={{ 
                                                    backgroundColor: message.type === 'user' ? '#e3f2fd' : '#f5f5f5',
                                                    p: 1,
                                                    borderRadius: 1,
                                                    whiteSpace: 'pre-wrap'
                                                }}>
                                                    {message.content}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    ))}
                                    {isGeneratingResponse && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                                                <SmartToy />
                                            </Avatar>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <CircularProgress size={16} />
                                                <Typography variant="body2" color="textSecondary">
                                                    AIが回答を生成中...
                                                </Typography>
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            )}
                            
                            {/* Question Input */}
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <TextField
                                    fullWidth
                                    multiline
                                    maxRows={3}
                                    placeholder="今月のデータについて質問してください（例：売上の改善点は？労働時間の効率性は？）"
                                    value={currentQuestion}
                                    onChange={(e) => setCurrentQuestion(e.target.value)}
                                    onKeyPress={handleQuestionKeyPress}
                                    disabled={isGeneratingResponse}
                                    sx={{ flex: 1 }}
                                />
                                <IconButton
                                    onClick={handleFollowUpQuestion}
                                    disabled={!currentQuestion.trim() || isGeneratingResponse}
                                    color="primary"
                                    sx={{ alignSelf: 'flex-end' }}
                                >
                                    <Send />
                                </IconButton>
                            </Box>
                            
                            {/* Quick Question Suggestions */}
                            {conversationHistory.length === 0 && (
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                                        よくある質問例：
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        {[
                                            '売上の改善点は？',
                                            '労働時間の効率性は？',
                                            'チーム評価の傾向は？',
                                            '来月の改善提案は？'
                                        ].map((suggestion, index) => (
                                            <Chip
                                                key={index}
                                                label={suggestion}
                                                size="small"
                                                variant="outlined"
                                                onClick={() => setCurrentQuestion(suggestion)}
                                                sx={{ cursor: 'pointer' }}
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            )}
                            
                            {/* Dynamic Suggestions */}
                            {conversationHistory.length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <Typography variant="caption" color="textSecondary">
                                            AIが提案する質問：
                                        </Typography>
                                        {isGeneratingSuggestions && (
                                            <CircularProgress size={12} />
                                        )}
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        {dynamicSuggestions.map((suggestion, index) => (
                                            <Chip
                                                key={index}
                                                label={suggestion}
                                                size="small"
                                                variant="outlined"
                                                onClick={() => setCurrentQuestion(suggestion)}
                                                sx={{ cursor: 'pointer' }}
                                                disabled={isGeneratingSuggestions}
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    )}
                </CardContent>
            </Card>

            <Box sx={{ display: 'flex', gap: 2, m: 5 }}>
                <Card sx={{ width: '50%' }}>
                    <CardContent>
                        <Typography variant="h7" component="div" sx={{ display: 'flex', alignContent: 'center', fontWeight: 'bold' }}>
                            <ShowChartOutlined sx={{ mr: 1 }} />
                            評価グラフ
                        </Typography>
                        {feedbacks.length ? 
                            <PieChart
                                width={400}
                                height={300}
                                series={[
                                    {
                                        data: [
                                            { id: 'good', label: `希望通り ${ratingCounts.good}人`, value: ratingRatios.good, color: '#3FC8E4' },
                                            { id: 'neutral', label: `まあまあ ${ratingCounts.neutral}人`, value: ratingRatios.neutral, color: '#5654D4' },
                                            { id: 'bad', label: `不満 ${ratingCounts.bad}人`, value: ratingRatios.bad, color: '#FB3B52' }
                                        ],
                                        type: 'pie',
                                        innerRadius: 65,
                                        outerRadius: 100,
                                        paddingAngle: 5,
                                        cornerRadius: 5,
                                        startAngle: 0,
                                        endAngle: 360,
                                        cx: 150,
                                        cy: 150,
                                        ...evaluationPieLabelProps
                                    }
                                ]}
                                sx={{
                                    [`& .${pieArcLabelClasses.root}`]: {
                                        fontWeight: 'bold'
                                    },                                
                                  }}
                                slotProps={{
                                    legend: {
                                        direction: 'column',
                                        position: { vertical: 'middle', horizontal: 'right' },
                                        padding: 0,
                                        itemMarkWidth: 20,
                                        itemMarkHeight: 20,
                                        markGap: 5,
                                        itemGap: 10
                                    }
                                }}
                            />
                            :
                            <Typography variant="h7" component="div" sx={{ mt: 3, display: 'flex', alignContent: 'center' }}>
                                今月はまだ評価がありません
                            </Typography>
                        }
                    </CardContent>
                </Card>

                <Card sx={{ width: '75%' }}>
                    <CardContent>
                        <Typography variant="h7" component="div" sx={{ display: 'flex', alignContent: 'center', fontWeight: 'bold' }}>
                            <TrendingUp sx={{ mr: 1 }} />
                            売上グラフ
                        </Typography>
                        {salesData.length ? 
                            <Box sx={{ width: '100%', height: 300 }}>
                                <LineChart
                                    width={undefined}
                                    height={300}
                                    series={[
                                        {
                                            data: salesChartData.map(item => item.sales),
                                            label: '実績売上 (千円)',
                                            color: '#9C27B0',
                                            curve: 'linear'
                                        },
                                        {
                                            data: salesChartData.map(item => item.prediction),
                                            label: '予測売上 (千円)',
                                            color: '#FF9800',
                                            curve: 'linear',
                                            lineStyle: 'dashed'
                                        }
                                    ]}
                                    xAxis={[
                                        {
                                            data: salesChartData.map(item => item.date),
                                            scaleType: 'band',
                                            label: '日付',
                                            tickInterval: weeklyDates
                                        }
                                    ]}
                                    yAxis={[
                                        {
                                            label: '売上 (千円)',
                                        }
                                    ]}
                                    slotProps={{
                                        legend: {
                                            direction: 'row',
                                            position: { vertical: 'top', horizontal: 'middle' },
                                            padding: 0
                                        }
                                    }}
                                />
                            </Box>
                            :
                            <Typography variant="h7" component="div" sx={{ mt: 3, display: 'flex', alignContent: 'center' }}>
                                今月はまだ売上データがありません
                            </Typography>
                        }
                    </CardContent>
                </Card>
            </Box>

            <Card sx={{ m: 5 }}>
                <CardContent>
                    <Typography variant="h7" component="div" sx={{ display: 'flex', alignContent: 'center', fontWeight: 'bold' }}>
                        <Schedule sx={{ mr: 1 }} />
                        合計労働時間グラフ
                    </Typography>
                    {hoursData.length ? 
                        <Box sx={{ width: '100%', height: 300 }}>
                            <LineChart
                                width={undefined}
                                height={300}
                                series={[
                                    {
                                        data: hoursChartData.map(item => item.actual),
                                        label: '実績時間 (時間)',
                                        color: '#9C27B0',
                                        curve: 'linear'
                                    },
                                    {
                                        data: hoursChartData.map(item => item.planned),
                                        label: '予定時間 (時間)',
                                        color: '#FF9800',
                                        curve: 'linear',
                                        lineStyle: 'dashed'
                                    }
                                ]}
                                xAxis={[
                                    {
                                        data: hoursChartData.map(item => item.date),
                                        scaleType: 'band',
                                        label: '日付',
                                        tickInterval: weeklyHoursDates
                                    }
                                ]}
                                yAxis={[
                                    {
                                        label: '労働時間 (時間)'
                                    }
                                ]}
                                slotProps={{
                                    legend: {
                                        direction: 'row',
                                        position: { vertical: 'top', horizontal: 'middle' },
                                        padding: 0
                                    }
                                }}
                            />
                        </Box>
                        :
                        <Typography variant="h7" component="div" sx={{ mt: 3, display: 'flex', alignContent: 'center' }}>
                            今月はまだ労働時間データがありません
                        </Typography>
                    }
                </CardContent>
            </Card>

            <Card sx={{ m: 5 }}>
                <CardContent>
                    <Typography variant="h7" component="div" sx={{ display: 'flex', alignContent: 'center', fontWeight: 'bold' }}>
                        <CommentOutlined sx={{ mr: 1 }} />
                        評価コメント
                    </Typography>
                    {feedbacks.length ? 
                        feedbacks.map((f, index) => (<Box key={index} display='flex' alignItems='center' sx={{ m:2 }}>
                            {iconMapping[f.rating]}
                            <Typography key={index} variant="body1" sx={{ ml: 1 }}>
                                {f.comment}
                            </Typography>
                        </Box>))
                        :
                        <Typography variant="h7" component="div" sx={{ mt: 3, display: 'flex', alignContent: 'center' }}>
                            今月はまだコメントがありません
                        </Typography>
                    }
                </CardContent>
            </Card>
        </PageWithTitleLayout>

    )
}

export default FeedbackManagementsDemo;